import type { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import type { IHighlightedTextArea } from '@pushly/aqe/lib/components/highlighted-text-area/highlighted-text-area'
import { bindingById } from '@pushly/models/lib/enums'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-tier-visual'
import { locateSpan } from './span-locator'

/** The shape aqe's HighlightedTextArea consumes. Taken FROM the component rather than restated
 * here, so a widening on its side (as `dimmedBackground` was) cannot fail to reach these maps.
 */
type TierStyle = IHighlightedTextArea['tierStyles'][string]

export type HighlightTier = 'checked' | 'needsReview'

/** All four compiled-directive tiers, including the two (`guaranteed`, `guidance`) that never
 * get per-item hover/click wiring in the editor drawer's decomposition rows — the display well
 * (see `findAllTierSpans` below) highlights all four, so it needs styles for all four.
 */
export type AnyHighlightTier = HighlightTier | 'guaranteed' | 'guidance'

export interface IHighlightSpan {
    key: string
    tier: HighlightTier
    start: number
    end: number
    /** The body text this span covered when it was compiled. Carried so the span can be checked
     * against — and re-found in — text the author has edited since. See {@link reconcileSpans}.
     */
    quote: string
}

/** Key for the Nth Guidance statement. `style` is now an array of independently-extracted
 * statements (it was one synthesized paragraph, which is what made the tier unstable), so each
 * gets its own key exactly like `assertions`/`unmapped`.
 */
export const guidanceSpanKey = (i: number) => `guidance-${i}`

export interface IAnyHighlightSpan {
    key: string
    tier: AnyHighlightTier
    start: number
    end: number
    /** See {@link IHighlightSpan.quote}. */
    quote: string
}

/**
 * Tier styles for aqe's tier-agnostic `HighlightedTextArea`, derived from the same
 * `BRAND_VOICE_TIER_VISUAL` the rule rows render from so the two can never visually disagree.
 *
 * Background alpha is the density lever, and the only one. Dark mode needs a different value
 * rather than the same one: a light-mode alpha over a near-black ground sits below the page in
 * luminance, darkening the text it should be lifting.
 *
 * CSS variables rather than colours computed here, because these are handed over as inline
 * `backgroundColor` and an inline style cannot answer a theme change. They resolve against
 * `[data-theme]`, which aqe's ThemeModeProvider stamps on <html> and which honours an explicit
 * user choice where `prefers-color-scheme` alone would not.
 *
 * Never solve density with `opacity` on a `<mark>` — it fades the TEXT along with the
 * background, so the author's own words go pale for having produced a rule.
 */
export const HIGHLIGHT_TIER_STYLES: Record<HighlightTier, TierStyle> = {
    checked: {
        background: `var(--sw-bv-wash-checked)`,
        dimmedBackground: `var(--sw-bv-dim-checked)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.checked.color,
    },
    needsReview: {
        background: `var(--sw-bv-wash-needsReview)`,
        dimmedBackground: `var(--sw-bv-dim-needsReview)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.needsReview.color,
    },
}

/** Same idea as `HIGHLIGHT_TIER_STYLES`, extended to all four tiers for the read-only display
 * well — see `AnyHighlightTier`.
 */
export const ALL_TIER_HIGHLIGHT_STYLES: Record<AnyHighlightTier, TierStyle> = {
    guaranteed: {
        background: `var(--sw-bv-wash-guaranteed)`,
        dimmedBackground: `var(--sw-bv-dim-guaranteed)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.guaranteed.color,
    },
    ...HIGHLIGHT_TIER_STYLES,
    guidance: {
        background: `var(--sw-bv-wash-guidance)`,
        dimmedBackground: `var(--sw-bv-dim-guidance)`,
        activeColor: BRAND_VOICE_TIER_VISUAL.guidance.color,
    },
}

/**
 * Spans come from the compiled directive's own attribution, resolved server-side at compile time
 * against the exact body that produced it. A client-side search cannot do this: a genuine
 * paraphrase leaves no verbatim substring to find, and only the compiler knows which input text
 * produced which rule.
 *
 * An empty `sources` array is expected, not a failure — a paraphrase has nothing to quote. It
 * contributes no span and its row stays non-interactive.
 *
 * One item can resolve to several spans; each becomes its own `HighlightSpan` sharing that
 * item's key, so matching stays per-item rather than per-span.
 */
export function findVerbatimSpans(compiled: Pick<CompiledDirective, 'assertions' | 'unmapped'>): IHighlightSpan[] {
    const spans: IHighlightSpan[] = []

    ;(compiled.assertions ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `checked-${i}`,
                tier: 'checked',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })
    ;(compiled.unmapped ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `needsReview-${i}`,
                tier: 'needsReview',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })

    return spans.sort((a, b) => a.start - b.start)
}

/**
 * Same idea as `findVerbatimSpans`, extended to all four tiers — for the read-only display well
 * (`BrandVoiceWell`), which highlights everything a directive compiled into, not just the two
 * tiers the editor drawer links to rows. `resolved` (Guaranteed) and `style` (Guidance) carry
 * `sources` the same way `assertions`/`unmapped` do; nothing new server-side was needed.
 *
 * Kept separate from `findVerbatimSpans` rather than widening it in place — the editor drawer's
 * decomposition rows only ever wire hover/click for Checked/Needs-review, so handing it
 * Guaranteed/Guidance spans it has no matching interactive rows for would just add
 * non-interactive marks to that view with no corresponding change requested there.
 */
export function findAllTierSpans(
    compiled: Pick<CompiledDirective, 'resolved' | 'assertions' | 'style' | 'unmapped'>,
): IAnyHighlightSpan[] {
    const spans: IAnyHighlightSpan[] = []

    for (const row of guaranteedDisplayRows(compiled)) {
        for (const source of row.sources) {
            spans.push({
                key: row.key,
                tier: 'guaranteed',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        }
    }
    ;(compiled.assertions ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `checked-${i}`,
                tier: 'checked',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })
    ;(compiled.style ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: guidanceSpanKey(i),
                tier: 'guidance',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })
    ;(compiled.unmapped ?? []).forEach((item, i) => {
        ;(item.sources ?? []).forEach((source) => {
            spans.push({
                key: `needsReview-${i}`,
                tier: 'needsReview',
                start: source.start,
                end: source.end,
                quote: source.quote,
            })
        })
    })

    // Deliberately NOT deduped: this list also answers "which items have attribution", via
    // `matchedSpanKeys`. Collapsing duplicate ranges here would drop the keys of every entry
    // after the first in a fan-out. See `dedupeSpansForRender`, applied at the render boundary.
    return spans.sort((a, b) => a.start - b.start)
}

/**
 * Drops or re-places spans so they describe the text currently on screen — see
 * {@link locateSpan} for how a span is placed and why an unplaceable one is dropped.
 *
 * Dropping also de-wires the row: `matchedSpanKeys` is built from this list, so a rule whose
 * source sentence has been rewritten stops offering a hover that would highlight nothing.
 */
export function reconcileSpans<T extends { start: number; end: number; quote: string }>(
    spans: T[],
    body: string,
    compiledBody?: string,
): T[] {
    const reconciled: T[] = []

    for (const span of spans) {
        const located = locateSpan(body, span, compiledBody)
        if (located) reconciled.push({ ...span, ...located })
    }

    return reconciled.sort((a, b) => a.start - b.start)
}

/**
 * Drops spans that would paint the exact same range twice within a tier — for RENDERING only.
 *
 * Kept separate from {@link findAllTierSpans} deliberately. That function is also the source of
 * `matchedSpanKeys`, i.e. "which items have attribution at all", and deduping there silently
 * dropped the keys of every entry after the first in a fan-out: a directive banning three words
 * produces three `resolved` entries that all quote the SAME sentence, so two of the three rows
 * lost their wiring despite being perfectly well attributed. Identical ranges still need to
 * collapse before rendering, because HighlightedTextArea walks a single monotonic cursor and
 * silently skips anything already consumed — but that is a painting concern, not an
 * attribution one.
 */
export function dedupeSpansForRender(spans: IAnyHighlightSpan[]): IAnyHighlightSpan[] {
    const seen = new Set<string>()
    return spans.filter((s) => {
        const id = `${s.tier}:${s.start}:${s.end}`
        if (seen.has(id)) return false
        seen.add(id)
        return true
    })
}

/**
 * One entry per row the Guaranteed section should SHOW, which is not one per PQuery condition.
 *
 * `banned_terms` is one `string_array` binding that the compiler fans out to one condition per
 * term, because `CONTENT_DIRECTIVE_PROFILE` is depth-1 and AND-only. Rendered literally that is
 * a wall of near-identical rows for what the author wrote as a sentence or two, pushing every
 * other tier below the fold. Collapsing them is more faithful to the compiled model, not less:
 * `resolved` holds one binding, and this restores it.
 *
 * Grouped by binding id and only for `string_array` — an integer limit or a flag is already one
 * rule per entry, and merging two length limits would be a lie.
 *
 * Sources are the ROW's, not the term's: every fanned-out entry carries the same source list,
 * since the compiler attributes the binding to every sentence that contributed a term.
 */
export interface IGuaranteedDisplayRow {
    /** Span key shared by every entry folded into this row. */
    key: string
    attribute: string
    operator: string
    /** An array when several terms were folded together, the entry's own value otherwise. */
    value: unknown
    sources: { start: number; end: number; quote: string }[]
}

export function guaranteedDisplayRows(compiled: Pick<CompiledDirective, 'resolved'>): IGuaranteedDisplayRow[] {
    const rows: IGuaranteedDisplayRow[] = []
    const groupedByBindingId = new Map<string, IGuaranteedDisplayRow>()

    ;(compiled.resolved ?? []).forEach((item, i) => {
        const binding = item.binding ? bindingById(item.binding.id) : undefined
        if (!binding) return

        const sources = (item.sources ?? []).map((s) => ({ start: s.start, end: s.end, quote: s.quote }))

        if (binding.value.kind !== 'string_array') {
            rows.push({
                key: `guaranteed-${i}`,
                attribute: binding.attribute,
                operator: binding.operator,
                value: item.binding.value,
                sources,
            })
            return
        }

        const existing = groupedByBindingId.get(binding.id)
        if (existing) {
            ;(existing.value as unknown[]).push(item.binding.value)
            // Sources repeat across the fan-out; keep one of each so a row does not paint the
            // same sentence several times over.
            for (const source of sources) {
                if (!existing.sources.some((s) => s.start === source.start && s.end === source.end)) {
                    existing.sources.push(source)
                }
            }
            return
        }

        // Keyed on the FIRST entry's index, so keys keep the same shape as every other tier's
        // and stay stable for a given compile.
        const row: IGuaranteedDisplayRow = {
            key: `guaranteed-${i}`,
            attribute: binding.attribute,
            operator: binding.operator,
            value: [item.binding.value],
            sources,
        }
        groupedByBindingId.set(binding.id, row)
        rows.push(row)
    })

    // Term sources are filtered once the row's full value list is known — see
    // `sourcesStatingATerm`.
    for (const row of groupedByBindingId.values()) {
        row.sources = sourcesStatingATerm(row.sources, row.value as unknown[])
    }

    return rows
}

/**
 * Keeps only the sentences that actually state one of the terms.
 *
 * The compiler attributes a `string_array` BINDING to every sentence it believes contributed,
 * and it over-reaches: `"www" should not be place before a web address` is do-not-use shaped, so
 * it gets listed as a banned-terms source despite naming no banned term. Measured in a real
 * directive, and the consequence was not cosmetic — that sentence is ALSO an assertion's source,
 * and `HighlightedTextArea` walks a single monotonic cursor, so the guaranteed span (pushed
 * first) painted the sentence green and the Checked span covering the same range was skipped
 * entirely. The row stayed in Checked, its mark never rendered, and hovering it lit nothing:
 * exactly the "interaction doesn't work" symptom, with no error anywhere.
 *
 * This is the precise half of what `narrowToLiteralValue` used to attempt. That function tried to
 * point at the literal WITHIN each quote and fell back to the whole sentence when it wasn't
 * there — which is how an unrelated sentence became a full-width highlight. Dropping the source
 * is the right answer to "this sentence contains none of these terms": we do not know that it
 * contributed, so we do not claim it.
 *
 * Case-insensitive, because the author's casing and the extracted term need not agree
 * ("Cutting-Edge" in prose, `cutting-edge` as the term).
 *
 * Returning EMPTY is a legitimate outcome — the row simply has no attribution and its PQuery row
 * stays non-interactive. Falling back to every quote would restore the over-claim this exists to
 * remove.
 */
function sourcesStatingATerm(
    sources: { start: number; end: number; quote: string }[],
    terms: unknown[],
): { start: number; end: number; quote: string }[] {
    const literals = terms.filter((t): t is string => typeof t === 'string' && t.length > 0).map((t) => t.toLowerCase())
    if (literals.length === 0) return sources

    return sources.filter((source) => {
        const quote = source.quote.toLowerCase()
        return literals.some((term) => quote.includes(term))
    })
}

/**
 * The condition group to hand PQueryBuilder — the collapsed rows, not the raw `spec`.
 *
 * Display only. The persisted `spec` is what actually gets enforced and is never touched: this
 * is the same set of rules with the fan-out folded back up for reading.
 *
 * An empty set is `{}` rather than `{ and: [] }`, matching what the compiler stores — the PQuery
 * reader rejects empty groups.
 */
export function collapsedGuaranteedSpec(compiled: Pick<CompiledDirective, 'resolved'>): Record<string, unknown> {
    const conditions = guaranteedDisplayRows(compiled).map((row) => ({
        [row.attribute]: { [row.operator]: row.value },
    }))

    return conditions.length > 0 ? { and: conditions } : {}
}

/**
 * Maps each Guaranteed condition, by its PQuery identity, to the span key of the display row
 * that produced it — so a rendered PQueryBuilder row can be linked back to the author's text.
 *
 * Keyed on `{property, operator, value}` rather than position, deliberately: the builder can
 * only read a condition off what it rendered, and the identity is what both sides can compute
 * independently. Derived from {@link guaranteedDisplayRows} so the grouping used to BUILD the
 * spec and the grouping used to LINK it can never disagree.
 */
export function guaranteedRowKeyIndex(compiled: Pick<CompiledDirective, 'resolved'>): Map<string, string> {
    const index = new Map<string, string>()
    for (const row of guaranteedDisplayRows(compiled)) {
        index.set(conditionIdentity(row.attribute, row.operator, row.value), row.key)
    }
    return index
}

/** Stable string form of a condition's identity — see {@link guaranteedRowKeyIndex}. */
export function conditionIdentity(property: string, operator: string, value: unknown): string {
    return `${property}|${operator}|${JSON.stringify(value ?? null)}`
}
